import { useTranslation } from 'react-i18next';

import useAiChatToolStatus from '../../hooks/useAiChatToolStatus';
import AiChatDatasourceTable from '../AiChatDatasourceTable';
import AiChatDisplayFailed from './AiChatDisplayFailed';
import AiChatDisplayPlaceholder from './AiChatDisplayPlaceholder';

import type { ReactElement } from 'react';
import type { IAiChatToolCallOf } from '../../interfaces/IAiChatToolCall';

/**
 * The `showDatasource` tool call: a stored result placed in the reply as a table.
 *
 * A display tool: the call is the display slot, and the reply shows it inline, not as a step.
 * While it runs, the title the model asked for over an empty slot; once it is done, the table
 * itself, its rows paged from the datasource route. A result of `createDatasource` is read the
 * same way: the rows live on the server, never in the part. A call that showed nothing leaves
 * one line saying so: the model is told why and writes the reply another way.
 */
function ShowDatasourceToolCall({ part }: IAiChatToolCallOf<'showDatasource'>): ReactElement | null {
  const { t } = useTranslation();
  const status = useAiChatToolStatus(part.state);

  if (part.state !== 'output-available') {
    return (
      <AiChatDisplayPlaceholder
        title={part.input?.title || t('aiChat.tools.showDatasource')}
        status={status}
        errorText={part.errorText}
      />
    );
  }

  if (!part.output.shown) {
    return <AiChatDisplayFailed type={t('aiChat.tools.displayTable')} />;
  }

  const {
    datasourceId, title, columns, rowCount, truncated,
  } = part.output;

  return (
    <AiChatDatasourceTable
      datasourceId={datasourceId}
      title={title}
      columns={columns}
      rowCount={rowCount}
      truncated={truncated}
    />
  );
}

export default ShowDatasourceToolCall;
